'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Icons from '@fortawesome/free-solid-svg-icons';

type Tag = {
  _id: string;
  name: string;
  color: string;
  icon?: string;
};

type ProductDetailsClientProps = {
  description: string;
  tags: Tag[];
  categoryName: string;
};

const PLACEHOLDER_VOUCHES = [
  'https://i.imgur.com/placeholder1.jpg',
  'https://i.imgur.com/placeholder2.jpg',
  'https://i.imgur.com/placeholder3.jpg',
  'https://i.imgur.com/placeholder4.jpg',
];

export default function ProductDetailsClient({ description, tags, categoryName }: ProductDetailsClientProps) {
  const [activeTab, setActiveTab] = useState<'description' | 'vouches'>('description');
  const [vouchImages, setVouchImages] = useState<string[]>(PLACEHOLDER_VOUCHES);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % vouchImages.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + vouchImages.length) % vouchImages.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  return (
    <div className="col pb-15 sm:pb-0 w-full lg:w-3xl my-6 lg:my-0 border-[0.5px] border-[rgba(249,115,22,.25)] bg-[rgb(31,39,52)] rounded-lg">
      {/* Tab Headers */}
      <div className="flex border-b border-[rgb(70,70,70)]">
        <button
          onClick={() => setActiveTab('description')}
          className={`flex-1 text-center text-xl p-3 transition-colors translation ${
            activeTab === 'description'
              ? 'text-white bg-orange-600/20 border-b-2 border-orange-600'
              : 'text-gray-400 hover:text-white hover:bg-orange-600/10'
          }`}
        >
          Description
        </button>
        <button
          onClick={() => setActiveTab('vouches')}
          className={`flex-1 text-center text-xl p-3 transition-colors translation ${
            activeTab === 'vouches'
              ? 'text-white bg-orange-600/20 border-b-2 border-orange-600'
              : 'text-gray-400 hover:text-white hover:bg-orange-600/10'
          }`}
        >
          Vouches
        </button>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'description' ? (
          <div>
            <div className="ml-[10px] mr-[10px] font-medium mb-6">
              <div className="whitespace-pre-wrap select-text">{description.replace(/\\n/g, '\n')}</div>
            </div>

            {/* Tags Section */}
            {tags.length > 0 && (
              <div className="ml-[10px] mr-[10px] mt-6 pt-4 border-t border-[rgb(70,70,70)]">
                <h3 className="text-sm text-gray-400 mb-3 font-semibold">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <div
                      key={tag._id}
                      className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium"
                      style={{ backgroundColor: `${tag.color}20`, border: `1px solid ${tag.color}` }}
                    >
                      {tag.icon ? (
                        <FontAwesomeIcon 
                          icon={(Icons as any)[tag.icon]} 
                          className="w-4 h-4" 
                          style={{ color: tag.color }} 
                        />
                      ) : (
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: tag.color }} 
                        />
                      )}
                      <span style={{ color: 'white' }}>{tag.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-semibold mb-4">Customer Vouches</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {vouchImages.map((imageUrl, index) => (
                <button
                  key={index}
                  onClick={() => openLightbox(index)}
                  className="relative aspect-video rounded-lg overflow-hidden border border-orange-600/25 hover:border-orange-600 transition-all cursor-pointer group"
                >
                  <Image
                    src={imageUrl}
                    alt={`Vouch ${index + 1}`}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={() => {
                      const newImages = [...vouchImages];
                      newImages[index] = 'https://i.imgur.com/YdIRwhV.png';
                      setVouchImages(newImages);
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <svg className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
            {vouchImages.length === 0 && (
              <p className="text-gray-400 text-center py-8">No vouches available yet.</p>
            )}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-[110] w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Previous Button */}
          {vouchImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 sm:left-4 z-[110] w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Previous"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Image Container */}
          <div
            className="relative w-full h-full max-w-6xl max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-full">
              <Image
                src={vouchImages[selectedImageIndex]}
                alt={`Vouch ${selectedImageIndex + 1}`}
                fill
                className="object-contain"
                priority
              />
            </div>
          </div>

          {/* Next Button */}
          {vouchImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 sm:right-4 z-[110] w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Next"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[110] px-4 py-2 bg-black/60 rounded-full text-white text-sm">
            {selectedImageIndex + 1} / {vouchImages.length}
          </div>
        </div>
      )}
    </div>
  );
}
